import React, { useState } from "react";

import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import { FaArrowLeft, FaSave, FaImage } from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import { uploadToCloudinary } from "../../../utils/uploadToCloudinary";

import { generateBlogSlug } from "../../../data/blogs";

import "./Blogs.css";

const AddBlog = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",

    slug: "",

    excerpt: "",

    content: "",

    category: "",

    author: "Quality Sports Surface",

    tags: "",

    seoTitle: "",

    seoDescription: "",

    image: "",

    status: "draft",

    published: false,

    featured: false,

    date: "",
  });

  const [imageFile, setImageFile] = useState(null);

  const [imagePreview, setImagePreview] = useState("");

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  // ============================================================
  // HANDLE INPUT
  // ============================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((previous) => ({
      ...previous,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ============================================================
  // TITLE
  // ============================================================

  const handleTitleChange = (e) => {
    const title = e.target.value;

    setFormData((previous) => ({
      ...previous,

      title,

      slug: generateBlogSlug(title),
    }));
  };

  // ============================================================
  // IMAGE
  // ============================================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");

      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10 MB.");

      return;
    }

    setError("");

    setImageFile(file);

    setImagePreview(URL.createObjectURL(file));
  };

  // ============================================================
  // SAVE BLOG
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!formData.title.trim()) {
      setError("Please enter a blog title.");

      return;
    }

    if (!formData.excerpt.trim()) {
      setError("Please enter a short description.");

      return;
    }

    if (!formData.content.trim()) {
      setError("Please enter blog content.");

      return;
    }

    if (!formData.category.trim()) {
      setError("Please enter a category.");

      return;
    }

    try {
      setSaving(true);

      // ======================================================
      // UPLOAD IMAGE
      // ======================================================

      let imageUrl = formData.image.trim();

      if (imageFile) {
        imageUrl = await uploadToCloudinary(imageFile, "image");
      }

      // ======================================================
      // TAGS
      // ======================================================

      const tagsArray = formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      // ======================================================
      // PUBLICATION
      // ======================================================

      const isPublished = formData.status === "published";

      // ======================================================
      // FIREBASE
      // ======================================================

      await addDoc(collection(db, "blogs"), {
        title: formData.title.trim(),

        slug: formData.slug.trim() || generateBlogSlug(formData.title),

        excerpt: formData.excerpt.trim(),

        content: formData.content.trim(),

        category: formData.category.trim(),

        author: formData.author.trim() || "Quality Sports Surface",

        tags: tagsArray,

        seoTitle: formData.seoTitle.trim() || formData.title.trim(),

        seoDescription:
          formData.seoDescription.trim() || formData.excerpt.trim(),

        image: imageUrl,

        status: formData.status,

        published: isPublished,

        featured: formData.featured,

        date: formData.date || "",

        createdAt: serverTimestamp(),

        updatedAt: serverTimestamp(),

        publishedAt: isPublished ? serverTimestamp() : null,
      });

      navigate("/admin/blogs");
    } catch (err) {
      console.error("Create blog error:", err);

      setError(err.message || "Unable to create blog.");
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="blog-form-page">
      {/* HEADER */}

      <div className="blog-form-header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/blogs")}
        >
          <FaArrowLeft />
          Back to Blogs
        </button>

        <h2>Create New Blog</h2>

        <p>Create an article for the Quality Sports Surface website.</p>
      </div>

      {/* ERROR */}

      {error && <div className="blog-form-error">{error}</div>}

      {/* FORM */}

      <form className="blog-form" onSubmit={handleSubmit}>
        {/* ====================================================
            BASIC INFORMATION
        ==================================================== */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Basic Information</h3>

            <p>Main information about your article.</p>
          </div>

          <div className="blog-form-body">
            {/* TITLE */}

            <div className="form-group">
              <label>Blog Title *</label>

              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="Enter blog title"
                disabled={saving}
              />
            </div>

            {/* SLUG */}

            <div className="form-group">
              <label>URL Slug</label>

              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="blog-url-slug"
                disabled={saving}
              />

              <small>Example: acrylic-vs-pu-sports-flooring</small>
            </div>

            {/* CATEGORY */}

            <div className="form-group">
              <label>Category *</label>

              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="Sports Flooring"
                disabled={saving}
              />
            </div>

            {/* AUTHOR */}

            <div className="form-group">
              <label>Author</label>

              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            {/* DATE */}

            <div className="form-group">
              <label>Publication Date</label>

              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            {/* EXCERPT */}

            <div className="form-group">
              <label>Short Description *</label>

              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="Write a short description..."
                rows="4"
                disabled={saving}
              />
            </div>

            {/* CONTENT */}

            <div className="form-group">
              <label>Blog Content *</label>

              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Write your complete blog article here..."
                rows="14"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* ====================================================
            FEATURED IMAGE
        ==================================================== */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>
              <FaImage />
              Featured Image
            </h3>

            <p>Upload an image for this blog.</p>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Upload Image</label>

              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={saving}
              />
            </div>

            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Blog preview" />
              </div>
            )}
          </div>
        </div>

        {/* ====================================================
            TAGS
        ==================================================== */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Tags</h3>

            <p>Separate multiple tags with commas.</p>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Blog Tags</label>

              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Sports Flooring, Acrylic, PU, Tennis Court"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* ====================================================
            SEO
        ==================================================== */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>SEO Settings</h3>

            <p>Improve how this article appears in search engines.</p>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>SEO Title</label>

              <input
                type="text"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                placeholder="SEO title"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>SEO Description</label>

              <textarea
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                placeholder="SEO description"
                rows="4"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* ====================================================
            PUBLISHING
        ==================================================== */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Publishing</h3>

            <p>Choose how this article should appear.</p>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Status</label>

              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={saving}
              >
                <option value="draft">Draft</option>

                <option value="published">Published</option>
              </select>
            </div>

            <label className="publish-toggle">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                disabled={saving}
              />

              <span>Mark this blog as featured</span>
            </label>
          </div>
        </div>

        {/* ACTIONS */}

        <div className="blog-form-actions">
          <button
            type="button"
            className="cancel-blog-btn"
            onClick={() => navigate("/admin/blogs")}
            disabled={saving}
          >
            Cancel
          </button>

          <button type="submit" className="save-blog-btn" disabled={saving}>
            <FaSave />

            {saving
              ? "Saving..."
              : formData.status === "published"
                ? "Publish Blog"
                : "Save Draft"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBlog;
